import { Gltf } from './GltfTypes';

const MODEL_TRANSFORM_KEY = '_MODEL_TRANSFORM';
const MESH_TRANSFORM_KEY = '_MESH_TRANSFORM';
const LEGACY_SKIN_KEY = '_MODEL_TRANSFORM';

// --- Model-level TRS (stored on root extras as _MODEL_TRANSFORM) ---

export interface ModelTransformExtras {
  position?: [number, number, number];
  /** Rotation quaternion [x, y, z, w]. */
  rotation?: [number, number, number, number];
  scale?: number;
}

export function readModelTransform(json: Gltf): ModelTransformExtras | null {
  // Primary: root extras
  const rootRaw = json.extras?.[MODEL_TRANSFORM_KEY];
  if (rootRaw && typeof rootRaw === 'object') {
    return validateTransform(rootRaw as ModelTransformExtras);
  }

  // Fallback: legacy skin extras (same key, different location)
  const skin = json.skins?.[0];
  if (!skin?.extras?.[LEGACY_SKIN_KEY]) return null;
  const skinRaw = skin.extras[LEGACY_SKIN_KEY];
  if (!skinRaw || typeof skinRaw !== 'object') return null;
  return validateTransform(skinRaw as ModelTransformExtras);
}

function validateTransform<T extends { rotation?: number[] }>(t: T): T {
  if (t.rotation && t.rotation.length !== 4) {
    throw new Error(
      `_MODEL_TRANSFORM/_MESH_TRANSFORM rotation must be a 4-element quaternion [x, y, z, w], got ${t.rotation.length} elements. Run the rotation migration CLI.`,
    );
  }
  return t;
}

export function writeModelTransform(json: Gltf, transform: ModelTransformExtras | null): Gltf {
  const clone = JSON.parse(JSON.stringify(json)) as Gltf;

  if (!clone.extras) {
    clone.extras = {};
  }

  if (
    transform &&
    (transform.position || transform.rotation || transform.scale !== undefined)
  ) {
    clone.extras[MODEL_TRANSFORM_KEY] = transform;
  } else {
    delete clone.extras[MODEL_TRANSFORM_KEY];
    if (Object.keys(clone.extras).length === 0) {
      delete clone.extras;
    }
  }

  return clone;
}

// --- Per-mesh TRS (stored on mesh.extras as _MESH_TRANSFORM) ---

export interface MeshTransformExtras {
  position?: [number, number, number];
  /** Rotation quaternion [x, y, z, w]. */
  rotation?: [number, number, number, number];
  scale?: number;
}

export function readMeshTransform(json: Gltf, meshName: string): MeshTransformExtras | null {
  const mesh = json.meshes?.find((m) => m.name === meshName);
  if (!mesh?.extras?.[MESH_TRANSFORM_KEY]) return null;
  const raw = mesh.extras[MESH_TRANSFORM_KEY];
  if (!raw || typeof raw !== 'object') return null;
  return validateTransform(raw as MeshTransformExtras);
}

export function readAllMeshTransforms(json: Gltf): Map<string, MeshTransformExtras> {
  const result = new Map<string, MeshTransformExtras>();
  if (!json.meshes) return result;
  for (const mesh of json.meshes) {
    const raw = mesh.extras?.[MESH_TRANSFORM_KEY];
    if (raw && typeof raw === 'object' && mesh.name) {
      result.set(mesh.name, validateTransform(raw as MeshTransformExtras));
    }
  }
  return result;
}

export function writeMeshTransform(
  json: Gltf,
  meshName: string,
  transform: MeshTransformExtras | null,
): Gltf {
  const clone = JSON.parse(JSON.stringify(json)) as Gltf;
  const mesh = clone.meshes?.find((m) => m.name === meshName);
  if (!mesh) return clone;

  if (!mesh.extras) {
    mesh.extras = {};
  }

  if (transform && (transform.position || transform.rotation || transform.scale !== undefined)) {
    mesh.extras[MESH_TRANSFORM_KEY] = transform;
  } else {
    delete mesh.extras[MESH_TRANSFORM_KEY];
    if (Object.keys(mesh.extras).length === 0) {
      delete mesh.extras;
    }
  }

  return clone;
}

// --- Deprecated legacy API (kept for existing callers during migration) ---

/** @deprecated Use {@link readModelTransform} instead. */
export function readModelTransformExtras(json: Gltf): ModelTransformExtras | null {
  return readModelTransform(json);
}

/** @deprecated Use {@link readModelTransform} instead. */
export function readModelScale(json: Gltf): number | null {
  const t = readModelTransform(json);
  return t?.scale ?? null;
}
